import { Plus, Trash } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "../components/ui/input";
import { TextArea } from "../components/ui/textarea";
import SortableOption from "../components/SortableAnswers";
import type { SurveyQuestion, SurveyOption } from "../types/defaultTypes";

type SortableQuestionProps = {
  question: SurveyQuestion;
  index: number;
  updateQuestion: (id: string, updates: Partial<SurveyQuestion>) => void;
  reorderOptions: (questionId: string, activeId: string, overId: string) => void;
  updateCorrect: (qId: string, type: SurveyQuestion["type"], oId: string) => void;
  updateOption: (qId: string, oId: string, val: string) => void;
  removeOption: (qId: string, oId: string) => void;
  removeQuestion: (qId: string) => void;
  addOption: (qId: string) => void;
  sensors: any; // from parent DndContext
  questionTypeLabel: (label: SurveyQuestion["type"]) => {
    header: string;
    answers: string;
    type: string;
  };
  errors?: {
    questionError?: string;
    optionErrors?: { id: string; error: string }[];
    settings?: {
      minChars?: string;
      maxChars?: string;
    };
  };
};

const SortableQuestion = ({
  question,
  index,
  updateQuestion,
  reorderOptions,
  updateCorrect,
  updateOption,
  removeOption,
  removeQuestion,
  addOption,
  sensors,
  questionTypeLabel,
  errors,
}: SortableQuestionProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { header, answers } = questionTypeLabel(question.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="p-6 bg-white rounded-2xl flex flex-col gap-5 shadow"
    >
      {/* Question Header */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <div className="h-8 w-8 shrink-0 bg-xuron-orange rounded-full text-white items-center justify-center flex">
            {question.order}
          </div>
          <div className="h-8 px-3 bg-xuron-orange rounded-full text-white items-center justify-center flex">
            {header}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => removeQuestion(question.id)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 border border-red-500 text-red-500"
          >
            <Trash className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Question Text */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-gray-500 text-sm font-medium">Question Text</p>
          <TextArea
            wrapperClass="z-0"
            className="!bg-input-bg !rounded-xl !min-h-[50px] w-full"
            value={question.text}
            error={errors?.questionError || ""}
            onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
          />
        </div>
      </div>

      <p className="text-sm font-medium text-gray-500">{answers}</p>

      {/* Options or Settings */}
      {question.type === "single-choice" || question.type === "multi-choice" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            reorderOptions(question.id, String(active.id), String(over.id));
          }}
        >
          <SortableContext
            items={question.options?.map((opt: SurveyOption) => opt.id) ?? []}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-2 w-full">
              {question.options?.map((opt) => {
                const optionError = errors?.optionErrors?.find((oe) => oe.id === opt.id);
                return (
                  <SortableOption
                    key={opt.id}
                    option={opt}
                    type={question.type === "single-choice" ? "radio" : "checkbox"}
                    checked={question.correctAnswers.includes(opt.id)}
                    updateCorrect={updateCorrect}
                    updateOption={updateOption}
                    removeOption={removeOption}
                    questionId={question.id}
                    questionType={question.type}
                    error={optionError?.error} // ⬅️ pass error down
                  />
                );
              })}
            </div>
          </SortableContext>

          <button
            className="text-blue-500 flex gap-2 items-center justify-center mt-2 px-4 py-3 border text-sm font-normal border-blue-500 rounded-xl bg-blue-100"
            onClick={() => addOption(question.id)}
          >
            <Plus className="w-4 h-4" /> Add Option
          </button>
        </DndContext>
      ) : (
        <div className="flex gap-6 items-center">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-normal text-gray-500">Min Characters</p>
            <Input
              className="!bg-input-bg"
              type="number"
              min={0}
              value={question?.settings?.minChars ?? ""}
              onChange={(e) =>
                updateQuestion(question.id, { settings: { ...question.settings, minChars: +e.target.value } })
              }
              error={errors?.settings?.minChars}
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-xs font-normal text-gray-500">Max Characters</p>
            <Input
              className="!bg-input-bg"
              type="number"
              min={0}
              value={question?.settings?.maxChars ?? ""}
              onChange={(e) =>
                updateQuestion(question.id, { settings: { ...question.settings, maxChars: +e.target.value } })
              }
              error={errors?.settings?.maxChars}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SortableQuestion;
